<template>
	<main class="shorts-detail">
		<div class="shorts-detail__layout">
			<section
				class="shorts-detail__video-wrap"
				:class="{ 'shorts-detail__video-wrap--panel-open': panelVisible }"
			>
				<div class="shorts-detail__video-inner">
					<video
						ref="videoRef"
						class="shorts-detail__video"
						:src="videoUrl"
						:poster="data?.cover"
						playsinline
						autoplay
						loop
						@click="togglePlay"
						@play="onVideoPlay"
						@playing="onVideoPlaying"
						@waiting="onVideoWaiting"
						@timeupdate="onVideoTimeUpdate"
						@loadedmetadata="handleLoadedMetadata"
					></video>

					<div class="shorts-detail__overlay shorts-detail__overlay--bottom">
						<div class="shorts-detail__author">
							<img
								:src="publisherInfo.avatar"
								:alt="publisherInfo.nickname"
								class="shorts-detail__avatar"
							/>
							<span class="shorts-detail__nickname">@{{ publisherInfo.nickname }}</span>
							<button
								type="button"
								class="shorts-detail__follow-btn"
								:class="{ 'shorts-detail__follow-btn--followed': isFollowed }"
								@click.stop="handleFollow"
							>
								{{ isFollowed ? t("square.detail.followed") : t("square.detail.follow") }}
							</button>
						</div>
						<p class="shorts-detail__caption" @click.stop="openDescriptionPanel">
							{{ captionText }}
						</p>
					</div>

					<div class="shorts-detail__progress">
						<div
							class="shorts-detail__progress-bar"
							:class="{ 'shorts-detail__progress-bar--loading': isVideoLoading }"
							:style="isVideoLoading ? undefined : { width: `${progressPercent}%` }"
						></div>
					</div>
				</div>

				<div class="shorts-detail__actions">
					<button
						type="button"
						class="shorts-detail__action"
						:class="{ 'shorts-detail__action--zaned': isZaned }"
						:disabled="squareZanLoading"
						@click.stop="handleZan"
					>
						<div class="shorts-detail__action-icon">
							<img :src="isZaned ? zanAfterIcon : zanBeforeIcon" alt="" />
						</div>
						<span>{{ t("square.detail.like") }}</span>
					</button>
					<button type="button" class="shorts-detail__action" @click.stop="onCommentClick">
						<div class="shorts-detail__action-icon">
							<img :src="pinlunIcon" alt="" />
						</div>
						<span>{{ t("square.detail.comments") }}</span>
					</button>
					<button type="button" class="shorts-detail__action" @click.stop="openSharePanel">
						<div class="shorts-detail__action-icon">
							<img :src="shareIcon" alt="" />
						</div>
						<span>{{ t("square.detail.share") }}</span>
					</button>
				</div>
			</section>

			<ShortsSidePanel
				v-if="panelVisible"
				:mode="panelMode"
				:data="data"
				@close="panelVisible = false"
			/>
		</div>

		<div class="shorts-detail__switch">
			<button
				type="button"
				class="shorts-detail__switch-btn"
				:disabled="!hasPrev || switching"
				:aria-label="t('square.pagination.prev')"
				@click="switchShort('prev')"
			>
				<img :src="prevIcon" alt="" />
			</button>
			<button
				type="button"
				class="shorts-detail__switch-btn"
				:disabled="!hasNext || switching"
				:aria-label="t('square.pagination.next')"
				@click="switchShort('next')"
			>
				<img :src="nextIcon" alt="" />
			</button>
		</div>

		<SharePanel v-model:show="showSharePanel" :share-list="shareList" @share="handleShare" />
	</main>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import { playContent } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { useSquare } from "~/composables/useSquare";
import SharePanel from "../share-panel/index.vue";
import ShortsSidePanel from "./side-panel.vue";
import {
	bucketCommentCount,
	buildDetailContentParams,
	usePlayProgressTracking,
} from "../../../composables/useSquareDetailTracking";
import { useShortsNavigation } from "../../../composables/useShortsNavigation";
import zanAfterIcon from "../../../images/zan_red.png";
import zanBeforeIcon from "../../../images/zan_before.png";
import pinlunIcon from "../../../images/pinlun2.png";
import shareIcon from "../../../images/sahre.png";
import prevIcon from "../../../images/shangyige.png";
import nextIcon from "../../../images/xiayige.png";

const props = defineProps({
	data: {
		type: Object,
		default: () => ({}),
	},
	squareId: {
		type: String,
		default: "",
	},
});

const { t } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const currentShortId = computed(() => props.squareId || props.data?.id);
const { hasPrev, hasNext, getAdjacentId } = useShortsNavigation(currentShortId);
const switching = ref(false);

const panelVisible = ref(false);
const panelMode = ref("desc");
const videoRef = ref(null);
const progressPercent = ref(0);
const isVideoLoading = ref(true);
const playStartTracked = ref(false);

const {
	handleTimeUpdate,
	trackPlayStart,
	reset: resetPlayProgress,
} = usePlayProgressTracking(logDetailEvent, () => props.data);

watch(
	() => props.data?.id,
	() => {
		panelVisible.value = false;
		panelMode.value = "desc";
		playStartTracked.value = false;
		isVideoLoading.value = true;
		progressPercent.value = 0;
		resetPlayProgress();
	}
);

const {
	isFollowed,
	isZaned,
	squareZanLoading,
	shareList,
	showSharePanel,
	handleFollow,
	handleZan,
	handleShare,
	openSharePanel,
} = useSquare(() => props.data);

const publisherInfo = computed(
	() =>
		props.data?.publisher_info || {
			nickname: "",
			avatar: "",
		}
);

const captionText = computed(() => {
	const title = props.data?.title || "";
	const desc = props.data?.desc || "";
	if (!desc || desc === title) return title;
	return `${title} | ${desc}`;
});

const getVideoUrl = mediaUrl => {
	if (!mediaUrl) return "";
	if (Array.isArray(mediaUrl) && mediaUrl.length > 0) {
		return String(mediaUrl[0]).replace(/\\\//g, "/");
	}
	if (typeof mediaUrl === "string") {
		try {
			const parsed = JSON.parse(mediaUrl);
			if (Array.isArray(parsed) && parsed.length > 0) {
				return String(parsed[0]).replace(/\\\//g, "/");
			}
		} catch {
			return mediaUrl.replace(/\\\//g, "/");
		}
	}
	return "";
};

const videoUrl = computed(() => getVideoUrl(props.data?.media_url));

const openDescriptionPanel = () => {
	panelMode.value = "desc";
	panelVisible.value = true;
};

const openComments = () => {
	panelMode.value = "comments";
	panelVisible.value = true;
};

const onCommentClick = () => {
	if (logDetailEvent) {
		logDetailEvent(
			"short_comment_click",
			buildDetailContentParams(props.data, {
				comment_count_bucket: bucketCommentCount(props.data?.comment_count),
			})
		);
	}
	openComments();
};

const togglePlay = () => {
	const video = videoRef.value;
	if (!video) return;
	if (video.paused) {
		video.play();
	} else {
		video.pause();
	}
};

const onVideoPlay = () => {
	if (playStartTracked.value) return;
	playStartTracked.value = true;
	trackPlayStart({ autoplay: true });
};

const onVideoPlaying = () => {
	isVideoLoading.value = false;
};

const onVideoWaiting = () => {
	const video = videoRef.value;
	if (!video || video.currentTime === 0) {
		isVideoLoading.value = true;
	}
};

const onVideoTimeUpdate = () => {
	const video = videoRef.value;
	if (!video || !video.duration) return;
	if (isVideoLoading.value && video.currentTime > 0) {
		isVideoLoading.value = false;
	}
	progressPercent.value = (video.currentTime / video.duration) * 100;
	handleTimeUpdate(video);
};

const handleLoadedMetadata = () => {
	progressPercent.value = 0;
};

const switchShort = async direction => {
	if (switching.value) return;

	const nextId = await getAdjacentId(direction);
	if (!nextId) return;

	switching.value = true;

	if (logDetailEvent) {
		logDetailEvent(
			"short_swipe_click",
			buildDetailContentParams(props.data, {
				direction: direction === "prev" ? "up" : "down",
				next_content_id: String(nextId),
			})
		);
	}

	try {
		await navigateTo(`/seer/square-detail/${nextId}`);
		await playContent({ square_id: nextId });
	} catch (error) {
		console.error("切换 Shorts 失败:", error);
	} finally {
		switching.value = false;
	}
};
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.shorts-detail {
	position: relative;
	display: flex;
	justify-content: center;
	padding: 40px 0 150px;

	&__switch {
		display: flex;
		flex-direction: column;
		gap: 50px;
		position: absolute;
		top: 50%;
		right: 100px;
		transform: translateY(-50%);
	}

	&__switch-btn {
		border: none;
		padding: 0;
		background: transparent;
		cursor: pointer;

		&:disabled {
			opacity: 0.35;
			cursor: not-allowed;
		}

		img {
			display: block;
			width: 48px;
			height: 48px;
		}
	}

	&__layout {
		display: flex;
		align-items: flex-start;
		gap: 24px;
	}

	&__video-wrap {
		flex-shrink: 0;
		display: flex;
		align-items: flex-end;
		gap: 16px;

		&--panel-open {
			position: relative;
			width: 508px;

			.shorts-detail__actions {
				position: absolute;
				right: 12px;
				bottom: 80px;
				padding-bottom: 0;
				color: #ffffff;
			}

			.shorts-detail__action-icon {
				background: rgba(0, 0, 0, 0.5);
			}

			.shorts-detail__action img {
				filter: brightness(0) invert(1);
			}

			.shorts-detail__action--zaned img {
				filter: none;
			}
		}
	}

	&__video-inner {
		position: relative;
		width: 508px;
		height: 905px;
		border-radius: 12px;
		overflow: hidden;
		background: #000000;
	}

	&__video {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		cursor: pointer;
	}

	&__overlay {
		width: 100%;
		position: absolute;
		left: 0;
		right: 0;
		pointer-events: none;

		&--bottom {
			bottom: 0;
			padding: 16px 16px 20px;
			background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0) 100%);
		}

		> * {
			pointer-events: auto;
		}
	}

	&__author {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}

	&__avatar {
		width: 48px;
		height: 48px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
	}

	&__nickname {
		font-size: 20px;
		font-weight: 500;
		color: #ffffff;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 160px;
	}

	&__follow-btn {
		flex-shrink: 0;
		padding: 4px 12px;
		border: 1px solid #ffffff;
		border-radius: 6px;
		background: #ffffff;
		color: #333333;
		font-size: 18px;
		cursor: pointer;
	}

	&__caption {
		margin: 0;
		font-size: 16px;
		color: #ffffff;
		line-height: 1.5;
		cursor: pointer;
		@include line-clamp(2);
	}

	&__video-wrap--panel-open &__overlay {
		right: 56px;
	}

	&__actions {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 20px;
		flex-shrink: 0;
		align-self: flex-end;
		padding-bottom: 80px;
		color: #333333;
	}

	&__action-icon {
		width: 56px;
		height: 56px;
		background: #f6f6f6;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__action {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		color: inherit;
		font-size: 16px;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		img {
			width: 40px;
			height: 40px;
			object-fit: contain;
			filter: brightness(0) saturate(100%);
		}

		&--zaned img {
			filter: none;
		}
	}

	&__progress {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 3px;
		overflow: hidden;
		background: rgba(255, 255, 255, 0.2);
	}

	&__progress-bar {
		height: 100%;
		background: #ff4757;
		transition: width 0.1s linear;

		&--loading {
			position: absolute;
			top: 0;
			left: 0;
			width: 35%;
			transition: none;
			animation: shorts-detail-progress-loading 1.2s ease-in-out infinite;
		}
	}
}

@keyframes shorts-detail-progress-loading {
	0% {
		left: -35%;
	}

	100% {
		left: 100%;
	}
}
</style>
